// src/pages/ProfilePage/ProfilePage.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import {
  User,
  Globe,
  Clock,
  Bell,
  Palette,
  Languages,
  Share2,
  Gift,
  MessageCircle,
  RefreshCw,
  Droplets,
  Flame,
  BarChart3,
  Sparkles,
  CheckCircle,
  AlertTriangle,
} from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { useRetinolStore } from '../../store/retinolStore';
import { useTelegramWebApp } from '../../hooks/useTelegramWebApp';
import { getUserTimezone, getUserTimeInfo, POPULAR_TIMEZONES } from '../../utils/helpers';
import { getEmojiAvatar, getSafeUserName } from '../../utils/avatarHelpers';
import { CONTAINER_PADDING, CONTAINER_PADDING_SMALL } from '../../utils/constants';
import { getDaysWord, formatDays } from '../../utils/pluralRu';
import { apiClient } from '../../services/api';
import {
  clearApplicationsFromCloud,
  syncNotificationsToCloud,
  getNotificationsFromCloud,
} from '../../utils/cloudStorage'; // ✅ ВАРИАНТ A: очистка CloudStorage + notifications sync

/** ================== UI types & normalization ================== */
type UIUser = {
  real_name: string | null; // ✅ БАГ 3: бекенд отдает real_name
  first_name: string | null; // Legacy (fallback)
  last_name: string | null;
  username: string | null;
  language_code: string | null;
  is_premium: boolean | null;
  timezone: string;
  telegram_id: number;
  photo_url: string | null;
};

// ============= ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ =============

/**
 * Вычисляет количество дней между двумя датами
 */
function getDaysBetween(date1: string, date2: string): number {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  const diffTime = d2.getTime() - d1.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Находит ближайшую незавершённую запланированную дату
 */
function getNextPlannedDate(
  plannedDates: string[],
  applications: Array<{ date: string; marked: boolean }>
): string | null {
  const today = new Date().toISOString().split('T')[0];

  // Фильтруем только будущие даты и сегодня, которые ещё не отмечены
  const upcomingDates = plannedDates
    .filter(date => {
      const isMarked = applications.some(app => app.date === date && app.marked);
      return date >= today && !isMarked;
    })
    .sort();

  return upcomingDates.length > 0 ? upcomingDates[0] : null;
}

// ============= STYLED COMPONENTS =============
const Container = styled.div`
  padding: ${CONTAINER_PADDING.top}px ${CONTAINER_PADDING.horizontal}px ${CONTAINER_PADDING.bottom}px;
  max-width: 430px;
  margin: 0 auto;
  /* ✅ КРИТИЧНО для iOS: используем динамический viewport вместо 100vh */
  min-height: var(--tg-viewport-stable-height, 100vh);
  background:
    radial-gradient(circle at 20% 20%, rgba(123, 104, 238, 0.08) 0%, transparent 50%),
    radial-gradient(circle at 80% 80%, rgba(135, 206, 235, 0.08) 0%, transparent 50%),
    #ffffff;

  [data-theme="dark"] & {
    background:
      radial-gradient(circle at 20% 20%, rgba(157, 142, 238, 0.12) 0%, transparent 55%),
      radial-gradient(circle at 80% 80%, rgba(160, 216, 240, 0.12) 0%, transparent 55%),
      var(--bg-primary);
  }

  @media (max-width: 375px) {
    padding: ${CONTAINER_PADDING_SMALL.top}px ${CONTAINER_PADDING_SMALL.horizontal}px ${CONTAINER_PADDING_SMALL.bottom}px;
  }
`;

// 👤 HEADER
const ProfileHeader = styled(motion.div)`
  text-align: center;
  margin-bottom: 24px;
  padding: 24px 20px;
  background: linear-gradient(135deg,
    rgba(255, 255, 255, 0.95),
    rgba(255, 255, 255, 0.8)
  );
  backdrop-filter: blur(20px);
  border: 1px solid rgba(123, 104, 238, 0.2);
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(123, 104, 238, 0.15);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #7B68EE, #87CEEB);
  }

  [data-theme="dark"] & {
    background: linear-gradient(135deg,
      rgba(42, 42, 60, 0.95),
      rgba(32, 32, 50, 0.8)
    );
    border: 1px solid rgba(196, 181, 253, 0.2);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);

    &::before {
      background: linear-gradient(90deg, #C4B5FD, #A0D8F0);
    }
  }

  @media (max-width: 375px) {
    padding: 20px 16px;
  }
`;

// ✅ Avatar с поддержкой фото пользователя
const Avatar = styled(motion.div)<{ $hasPhoto?: boolean }>`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: ${props => props.$hasPhoto
    ? 'transparent'
    : 'linear-gradient(135deg, #7B68EE, #9966CC)'
  };
  color: white !important;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 36px;
  font-weight: bold;
  margin: 0 auto 16px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 4px 16px rgba(123, 104, 238, 0.3);
  overflow: hidden;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const UserName = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: var(--tg-theme-text-color, #000000);
  margin: 0 0 4px 0;
  
  @media (max-width: 375px) {
    font-size: 20px;
  }
`;

const UserUsername = styled.p`
  font-size: 14px;
  color: rgba(0,0,0,0.6);
  margin: 0 0 8px 0;

  [data-theme="dark"] & {
    color: rgba(255, 255, 255, 0.6);
  }
`;

const UserStatus = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(123, 104, 238, 0.1);
  padding: 6px 12px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 600;
  color: #7B68EE !important;
  border: 1px solid rgba(123, 104, 238, 0.2);
  margin-top: 8px;

  svg {
    color: #7B68EE !important;
  }

  [data-theme="dark"] & {
    background: rgba(196, 181, 253, 0.15);
    color: #C4B5FD !important;
    border: 1px solid rgba(196, 181, 253, 0.2);

    svg {
      color: #C4B5FD !important;
    }
  }
`;

// 📊 STATS
const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-bottom: 20px;
  
  @media (max-width: 375px) {
    gap: 8px;
  }
`;

const StatCard = styled(motion.div)`
  background: linear-gradient(135deg,
    rgba(255, 255, 255, 0.95),
    rgba(255, 255, 255, 0.8)
  );
  backdrop-filter: blur(20px);
  border: 1px solid rgba(123, 104, 238, 0.15);
  border-radius: 16px;
  padding: 16px 12px;
  text-align: center;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(123, 104, 238, 0.2);
  }

  [data-theme="dark"] & {
    background: linear-gradient(135deg,
      rgba(42, 42, 60, 0.95),
      rgba(32, 32, 50, 0.8)
    );
    border: 1px solid rgba(196, 181, 253, 0.15);

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
    }
  }

  @media (max-width: 375px) {
    padding: 14px 10px;
  }
`;

const StatIcon = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 8px;
  color: #7B68EE !important;

  svg {
    color: #7B68EE !important;
  }

  [data-theme="dark"] & {
    color: #C4B5FD !important;

    svg {
      color: #C4B5FD !important;
    }
  }

  @media (max-width: 375px) {
    font-size: 22px;
  }
`;

const StatValue = styled.div`
  font-size: 22px;
  font-weight: 800;
  background: linear-gradient(135deg, #7B68EE, #9966CC);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 4px;
  line-height: 1;

  [data-theme="dark"] & {
    background: linear-gradient(135deg, #C4B5FD, #D8B4FE);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  @media (max-width: 375px) {
    font-size: 20px;
  }
`;

const StatLabel = styled.div`
  font-size: 11px;
  color: rgba(0,0,0,0.7);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  [data-theme="dark"] & {
    color: rgba(255, 255, 255, 0.7);
  }

  @media (max-width: 375px) {
    font-size: 10px;
  }
`;

// 📋 SECTIONS
const Section = styled.section`
  margin-bottom: 20px;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #000;
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    color: #7B68EE !important;
  }

  [data-theme="dark"] & {
    color: #ffffff;

    svg {
      color: #C4B5FD !important;
    }
  }
`;

const InfoCard = styled(motion.div)`
  background: linear-gradient(135deg,
    rgba(255, 255, 255, 0.95),
    rgba(255, 255, 255, 0.8)
  );
  backdrop-filter: blur(20px);
  border: 1px solid rgba(123, 104, 238, 0.15);
  border-radius: 16px;
  padding: 16px;
  margin-bottom: 12px;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(123, 104, 238, 0.2);
  }

  [data-theme="dark"] & {
    background: linear-gradient(135deg,
      rgba(42, 42, 60, 0.95),
      rgba(32, 32, 50, 0.8)
    );
    border: 1px solid rgba(196, 181, 253, 0.15);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
    }
  }

  @media (max-width: 375px) {
    padding: 14px;
  }
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid rgba(123, 104, 238, 0.08);

  &:last-child {
    border-bottom: none;
  }

  [data-theme="dark"] & {
    border-bottom: 1px solid rgba(196, 181, 253, 0.08);
  }

  @media (max-width: 375px) {
    padding: 8px 0;
  }
`;

const InfoRowClickable = styled(InfoRow)`
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 12px 8px;
  margin: 0 -8px;
  border-radius: 8px;

  &:hover {
    background: rgba(123, 104, 238, 0.05);
    transform: translateX(2px);
  }

  &:active {
    transform: scale(0.98);
  }

  [data-theme="dark"] &:hover {
    background: rgba(196, 181, 253, 0.08);
  }
`;

const InfoLabel = styled.span`
  font-size: 14px;
  color: rgba(0,0,0,0.7);
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    color: #7B68EE !important;
    flex-shrink: 0;
  }

  [data-theme="dark"] & {
    color: rgba(255, 255, 255, 0.7);

    svg {
      color: #C4B5FD !important;
    }
  }

  @media (max-width: 375px) {
    font-size: 13px;
  }
`;

const InfoValue = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #000;
  display: inline-flex;
  align-items: center;

  [data-theme="dark"] & {
    color: #ffffff;
  }

  @media (max-width: 375px) {
    font-size: 13px;
  }
`;

// 🎯 ACTIONS
const ActionButton = styled(motion.button)`
  width: 100%;
  padding: 14px 20px;
  background: linear-gradient(135deg,
    rgba(255, 255, 255, 0.95),
    rgba(255, 255, 255, 0.8)
  );
  backdrop-filter: blur(20px);
  color: #7B68EE !important;
  border: 2px solid rgba(123, 104, 238, 0.2);
  border-radius: 14px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 10px;
  transition: all 0.3s ease;

  svg {
    color: #7B68EE !important;
  }

  [data-theme="dark"] & {
    background: linear-gradient(135deg,
      rgba(42, 42, 60, 0.95),
      rgba(32, 32, 50, 0.8)
    );
    color: #C4B5FD !important;
    border: 2px solid rgba(196, 181, 253, 0.2);

    svg {
      color: #C4B5FD !important;
    }

    &:hover {
      border-color: rgba(196, 181, 253, 0.4);
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
    }
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(123, 104, 238, 0.2);
    border-color: rgba(123, 104, 238, 0.4);
  }

  @media (max-width: 375px) {
    padding: 12px 18px;
    font-size: 14px;
  }
`;

const PrimaryButton = styled(ActionButton)`
  background: linear-gradient(135deg, #7B68EE, #9966CC);
  color: white !important;
  border: none;

  svg {
    color: white !important;
  }

  [data-theme="dark"] & {
    background: linear-gradient(135deg, #C4B5FD, #D8B4FE);
    color: #000000 !important;

    svg {
      color: #000000 !important;
    }

    &:hover {
      box-shadow: 0 8px 24px rgba(196, 181, 253, 0.4);
    }
  }

  &:hover {
    box-shadow: 0 8px 24px rgba(123, 104, 238, 0.4);
  }
`;

const EditButton = styled(motion.button)`
  margin-left: 8px;
  font-size: 16px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #7B68EE !important;

  svg {
    color: #7B68EE !important;
  }

  [data-theme="dark"] & {
    color: #C4B5FD !important;

    svg {
      color: #C4B5FD !important;
    }
  }
`;

const TimezoneSelector = styled(motion.div)`
  margin-top: 16px;
  padding: 16px;
  background: rgba(123, 104, 238, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(123, 104, 238, 0.1);
  overflow: hidden;

  /* ✅ Оптимизация рендеринга для плавной анимации */
  will-change: transform, opacity;
  content-visibility: auto;
  contain: content;

  [data-theme="dark"] & {
    background: rgba(196, 181, 253, 0.08);
    border: 1px solid rgba(196, 181, 253, 0.15);
  }
`;

const SelectorTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #000;
  margin-bottom: 12px;

  [data-theme="dark"] & {
    color: #ffffff;
  }
`;

const TimezoneOption = styled(motion.button)`
  width: 100%;
  padding: 12px;
  margin-bottom: 8px;
  border-radius: 8px;
  border: 2px solid rgba(123, 104, 238, 0.2);
  background: white;
  color: #000 !important;
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
  text-align: left;
  transition: all 0.2s ease;

  &:hover {
    border-color: #7B68EE;
  }

  &[data-active="true"] {
    border-color: #7B68EE;
    background: rgba(123, 104, 238, 0.1);
    color: #7B68EE !important;
    font-weight: 600;
  }

  [data-theme="dark"] & {
    background: rgba(42, 42, 60, 0.5);
    border: 2px solid rgba(196, 181, 253, 0.2);
    color: #ffffff !important;

    &:hover {
      border-color: #C4B5FD;
    }

    &[data-active="true"] {
      border-color: #C4B5FD;
      background: rgba(196, 181, 253, 0.15);
      color: #C4B5FD !important;
      font-weight: 600;
    }
  }
`;

const CancelButton = styled.button`
  width: 100%;
  padding: 10px;
  margin-top: 8px;
  border-radius: 8px;
  border: none;
  background: rgba(0, 0, 0, 0.05);
  color: #000 !important;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  [data-theme="dark"] & {
    background: rgba(255, 255, 255, 0.1);
    color: #ffffff !important;

    &:hover {
      background: rgba(255, 255, 255, 0.15);
    }
  }

  &:hover {
    background: rgba(0, 0, 0, 0.1);
  }
`;

// ===== Toggle =====
const ToggleContainer = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

const ToggleTrack = styled(motion.button)<{ $isActive: boolean }>`
  width: 48px;
  height: 26px;
  border-radius: 13px;
  border: none;
  cursor: pointer;
  position: relative;
  transition: all 0.3s ease;
  background: ${props => props.$isActive
    ? 'linear-gradient(135deg, #7B68EE, #9966CC)'
    : 'rgba(0, 0, 0, 0.15)'
  };

  [data-theme="dark"] & {
    background: ${props => props.$isActive
      ? 'linear-gradient(135deg, #C4B5FD, #D8B4FE)'
      : 'rgba(255, 255, 255, 0.15)'
    };
  }

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ToggleThumb = styled(motion.div)<{ $isActive: boolean }>`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  position: absolute;
  top: 2px;
  left: ${props => props.$isActive ? '24px' : '2px'};
  transition: left 0.3s ease;

  [data-theme="dark"] & {
    background: ${props => props.$isActive ? '#000000' : '#ffffff'};
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.4);
  }
`;

const HintText = styled.p`
  margin-top: 12px;
  font-size: 13px;
  color: rgba(0,0,0,0.7) !important;
  line-height: 1.5;
  padding: 12px;
  background: rgba(123, 104, 238, 0.05);
  border-radius: 8px;
  border-left: 3px solid #7B68EE;
  display: flex;
  align-items: flex-start;
  gap: 8px;

  svg {
    color: #7B68EE !important;
    flex-shrink: 0;
    margin-top: 2px;
  }

  * {
    color: rgba(0,0,0,0.7) !important;
  }

  [data-theme="dark"] & {
    background: rgba(196, 181, 253, 0.08);
    border-left: 3px solid #C4B5FD;
    color: rgba(255, 255, 255, 0.85) !important;

    svg {
      color: #C4B5FD !important;
    }

    * {
      color: rgba(255, 255, 255, 0.85) !important;
    }
  }
`;

// ===== Modals =====
const ModalOverlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 9999;
`;

const ModalCard = styled(motion.div)`
  background: linear-gradient(135deg,
    rgba(255, 255, 255, 0.98),
    rgba(255, 255, 255, 0.95)
  );
  backdrop-filter: blur(20px);
  border: 1px solid rgba(123, 104, 238, 0.2);
  border-radius: 24px;
  padding: 32px 24px;
  max-width: 360px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(123, 104, 238, 0.3);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #7B68EE, #87CEEB);
  }

  [data-theme="dark"] & {
    background: linear-gradient(135deg,
      rgba(42, 42, 60, 0.98),
      rgba(32, 32, 50, 0.95)
    );
    border: 1px solid rgba(196, 181, 253, 0.2);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);

    &::before {
      background: linear-gradient(90deg, #C4B5FD, #A0D8F0);
    }
  }
`;

const ModalTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #000;
  margin: 0 0 12px 0;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  svg {
    flex-shrink: 0;
  }

  [data-theme="dark"] & {
    color: #ffffff;

    svg {
      color: #C4B5FD;
    }
  }
`;

const ModalText = styled.p`
  font-size: 15px;
  color: rgba(0,0,0,0.7) !important;
  line-height: 1.6;
  margin: 0 0 16px 0;
  text-align: center;

  * {
    color: rgba(0,0,0,0.7) !important;
  }

  [data-theme="dark"] & {
    color: rgba(255, 255, 255, 0.85) !important;

    * {
      color: rgba(255, 255, 255, 0.85) !important;
    }
  }
`;

const ModalButtonGroup = styled.div`
  display: flex;
  gap: 12px;
`;

const FeedbackTextarea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 12px 16px;
  border: 2px solid rgba(123, 104, 238, 0.2);
  border-radius: 12px;
  font-size: 15px;
  font-family: inherit;
  color: #000;
  background: rgba(255, 255, 255, 0.95);
  resize: vertical;
  margin-bottom: 16px;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #7B68EE;
    box-shadow: 0 0 0 3px rgba(123, 104, 238, 0.1);
  }

  &::placeholder {
    color: rgba(0, 0, 0, 0.4);
  }

  [data-theme="dark"] & {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.05);
    border: 2px solid rgba(196, 181, 253, 0.2);

    &:focus {
      border-color: #C4B5FD;
      box-shadow: 0 0 0 3px rgba(196, 181, 253, 0.1);
    }

    &::placeholder {
      color: rgba(255, 255, 255, 0.4);
    }
  }
`;

const ModalButton = styled(motion.button)<{ $variant: 'primary' | 'secondary' }>`
  flex: 1;
  padding: 14px 20px;
  border-radius: 14px;
  border: none;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  background: ${props => props.$variant === 'primary'
    ? 'linear-gradient(135deg, #7B68EE, #9966CC)'
    : 'rgba(123, 104, 238, 0.1)'
  };

  color: ${props => props.$variant === 'primary'
    ? '#FFFFFF !important'
    : '#7B68EE !important'
  };

  box-shadow: ${props => props.$variant === 'primary'
    ? '0 8px 24px rgba(123, 104, 238, 0.3)'
    : 'none'
  };

  &:hover {
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.7;
    cursor: default;
  }

  [data-theme="dark"] & {
    background: ${props => props.$variant === 'primary'
      ? 'linear-gradient(135deg, #C4B5FD, #D8B4FE)'
      : 'rgba(196, 181, 253, 0.15)'
    };

    color: ${props => props.$variant === 'primary'
      ? '#000000 !important'
      : '#C4B5FD !important'
    };

    box-shadow: ${props => props.$variant === 'primary'
      ? '0 8px 24px rgba(196, 181, 253, 0.3)'
      : 'none'
    };
  }
`;

// ============= COMPONENT =============
export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, updateTimezone, updateUser, logout, setQuestionnaireCompleted } = useAuthStore();
  const { webApp, user: telegramUser } = useTelegramWebApp();

  const {
    getStatistics,
    incrementShareCount,
    incrementReferralCount,
    incrementFeedbackCount,
    resetAchievements,
    resetProfile,
    plannedDates,
    applications,
  } = useRetinolStore((state: any) => ({
    getStatistics: state.getStatistics,
    incrementShareCount: state.incrementShareCount,
    incrementReferralCount: state.incrementReferralCount,
    incrementFeedbackCount: state.incrementFeedbackCount,
    resetAchievements: state.resetAchievements,
    resetProfile: state.resetProfile,
    plannedDates: state.plannedDates ||[],
    applications: state.applications ||[],
  }));

  /** Normalize user → UIUser to avoid union property errors */
  const raw = (user ?? undefined) as Partial<UIUser & { id_telegram?: number }> | undefined;

  const uiUser: UIUser = {
    real_name: raw?.real_name ?? null,      // ✅ БАГ 3: ТОЛЬКО из БД
    first_name: raw?.first_name ?? null,    // ТОЛЬКО из БД (legacy)
    last_name: raw?.last_name ?? null,
    username: raw?.username ?? null,
    language_code: raw?.language_code ?? telegramUser?.language_code ?? 'ru',
    is_premium: raw?.is_premium ?? telegramUser?.is_premium ?? null,
    timezone: raw?.timezone ?? getUserTimezone(),
    telegram_id:
      raw?.telegram_id ?? raw?.id_telegram ?? (typeof telegramUser?.id === 'number' ? telegramUser.id : 0),
    photo_url: raw?.photo_url ?? null,      // ✅ БАГ 4: ТОЛЬКО из БД (будет обновлено из /get_user_info)
  };

  const stats = getStatistics();

  const[showCopyModal, setShowCopyModal] = useState(false);
  const[showResetModal, setShowResetModal] = useState(false);
  const[showFeedbackModal, setShowFeedbackModal] = useState(false);
  const[feedbackText, setFeedbackText] = useState('');
  const[isSendingFeedback, setIsSendingFeedback] = useState(false);

  // ✅ БАГ 3: Состояние для уведомлений
  const[notificationsEnabled, setNotificationsEnabled] = useState<boolean>(
    (user as any)?.need_notifications ?? true
  );
  const[isUpdatingNotifications, setIsUpdatingNotifications] = useState(false);

  // ✅ БАГ: Состояние загрузки данных пользователя (real_name из API)
  const[isLoadingUserInfo, setIsLoadingUserInfo] = useState(true);

  // ✅ БАГ 3 FIX: Правильная логика отображения имени (real_name → first_name → username → Гость)
  // Показываем "Гость" пока загружаем данные из API, чтобы не было мигания username → real_name
  const avatarEmoji = getEmojiAvatar(uiUser.telegram_id);

  const getDisplayName = (): string => {
    // Пока загружаем данные из API - показываем "Гость"
    if (isLoadingUserInfo) {
      return 'Гость';
    }

    // Строгий приоритет: real_name → first_name → username → "Гость"
    if (uiUser.real_name && uiUser.real_name.trim()) {
      return getSafeUserName(uiUser.real_name);
    }

    if (uiUser.first_name && uiUser.first_name.trim()) {
      return getSafeUserName(uiUser.first_name);
    }

    if (uiUser.username && uiUser.username.trim()) {
      return uiUser.username;
    }

    return 'Гость';
  };

  const displayName = getDisplayName();
  const hasPhotoUrl = Boolean(uiUser.photo_url);

  // ✅ Вычисляем статус "Следующее применение"
  const getNextApplicationStatus = (): string => {
    const nextDate = getNextPlannedDate(plannedDates, applications);

    if (!nextDate) {
      // Нет запланированных дат или все выполнены
      if (plannedDates.length === 0) {
        return 'Запланируйте первое применение';
      }
      return 'Все применения выполнены! 🎉';
    }

    const today = new Date().toISOString().split('T')[0];

    if (nextDate === today) {
      return 'Следующее применение: сегодня';
    }

    const daysUntil = getDaysBetween(today, nextDate);

    if (daysUntil === 1) {
      return 'Следующее применение: завтра';
    }

    return `Следующее применение: через ${daysUntil} ${getDaysWord(daysUntil)}`;
  };

  const nextApplicationStatus = getNextApplicationStatus();

  // ✅ Обратная связь: открываем модальное окно
  const handleFeedbackClick = () => {
    setShowFeedbackModal(true);
    setFeedbackText('');
  };

  // ✅ Отправка обратной связи на API
  const handleSendFeedback = async () => {
    if (!feedbackText.trim() || isSendingFeedback) return;

    setIsSendingFeedback(true);

    try {
      const response = await apiClient.submitFeedback({
        feedback_text: feedbackText.trim(),
      });

      if (response.success) {
        hapticFeedback('success');
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ Обратная связь отправлена');
        }
        setShowFeedbackModal(false);
        setFeedbackText('');
        incrementFeedbackCount();
      } else {
        console.error('❌ Ошибка отправки обратной связи');
        hapticFeedback('error');
      }
    } catch (error) {
      console.error('❌ Ошибка при отправке обратной связи:', error);
      hapticFeedback('error');
    } finally {
      setIsSendingFeedback(false);
    }
  };

  const hapticFeedback = (type: 'success' | 'warning' | 'error' = 'success') => {
    try {
      webApp?.HapticFeedback?.notificationOccurred?.(type);
    } catch {
      // no-op
    }
    if (navigator.vibrate) {
      navigator.vibrate(40);
    }
  };

  const handleShare = async () => {
    const shareText = `
✨ Мой путь к идеальной коже с Geltek!

📊 Мои результаты за ${stats.totalApplications} дней:
- Прогресс: ${stats.completionRate}%
- Применений выполнено: ${stats.totalApplications}

🌟 Ретинол изменил мою кожу:
✓ Ровный тон
✓ Сияние изнутри
✓ Гладкая текстура

💜 Хочешь такой же результат?
Начни свой путь к совершенной коже!

👉 @geltek_kosmetolog_bot - твой персональный трекер ухода

#Geltek #КалендарьРетинола
    `.trim();

    // ✅ ШАГ 1: Копируем текст в буфер обмена (работает на всех платформах)
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(shareText);
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ Текст скопирован в буфер обмена');
        }
      } else {
        // Fallback для старых браузеров
        const textArea = document.createElement('textarea');
        textArea.value = shareText;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ Текст скопирован через fallback');
        }
      }
    } catch (error) {
      console.warn('⚠️ Не удалось скопировать текст:', error);
    }

    // ✅ ШАГ 2: Открываем нативный chooser Telegram через openTelegramLink
    const encodedText = encodeURIComponent(shareText);
    const botLink = 'https://t.me/geltek_kosmetolog_bot';
    const encodedUrl = encodeURIComponent(botLink);
    const shareUrl = `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`;
    
    if (webApp && typeof webApp.openTelegramLink === 'function') {
      try {
        webApp.openTelegramLink(shareUrl);
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ Открыт диалог шаринга через openTelegramLink');
        }
      } catch (error) {
        console.error('❌ Ошибка openTelegramLink:', error);
        // Fallback: пробуем через window.open (может сработать на некоторых платформах)
        window.open(shareUrl, '_blank');
      }
    } else {
      // Fallback для старых версий Telegram или если WebApp недоступен
      if (process.env.NODE_ENV === 'development') {
        console.warn('⚠️ openTelegramLink недоступен, используем fallback');
      }
      window.open(shareUrl, '_blank');
    }

    incrementShareCount();
    hapticFeedback('success');
  };

  const handleReferral = async () => {
    // ✅ Формируем текст приглашения с реферальной ссылкой
    const botUsername = 'geltek_kosmetolog_bot';
    const referralLink = `https://t.me/${botUsername}?start=invite_${uiUser.telegram_id}`;
    const referralText = `Использую ретинол от Geltek ✨ Вижу результат и рекомендую попробовать!\n\n${referralLink}`;

    // ✅ ШАГ 1: Копируем текст в буфер обмена (работает на всех платформах)
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(referralText);
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ Реферальная ссылка скопирована в буфер обмена');
        }
      } else {
        // Fallback для старых браузеров
        const textArea = document.createElement('textarea');
        textArea.value = referralText;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ Реферальная ссылка скопирована через fallback');
        }
      }
    } catch (error) {
      console.warn('⚠️ Не удалось скопировать ссылку:', error);
    }

    // ✅ ШАГ 2: Открываем нативный chooser Telegram через openTelegramLink
    const encodedUrl = encodeURIComponent(referralLink);
    const encodedText = encodeURIComponent('Использую ретинол от Geltek ✨ Вижу результат и рекомендую попробовать!');
    const shareUrl = `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`;

    if (webApp && typeof webApp.openTelegramLink === 'function') {
      try {
        webApp.openTelegramLink(shareUrl);
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ Открыт диалог приглашения через openTelegramLink');
        }
      } catch (error) {
        console.error('❌ Ошибка openTelegramLink:', error);
        // Fallback: пробуем через window.open (может сработать на некоторых платформах)
        window.open(shareUrl, '_blank');
      }
    } else {
      // Fallback для старых версий Telegram или если WebApp недоступен
      if (process.env.NODE_ENV === 'development') {
        console.warn('⚠️ openTelegramLink недоступен, используем fallback');
      }
      window.open(shareUrl, '_blank');
    }

    incrementReferralCount();
    hapticFeedback('success');
  };

  const handleResetClick = () => setShowResetModal(true);
  const handleResetConfirm = async () => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔄 Начинаем процесс сброса данных...');
    }

    try {
      // ✅ FIX: Сначала сбрасываем на сервере, локально очищаем только при успехе
      if (process.env.NODE_ENV === 'development') {
        console.log('📤 Отправляем POST /reset на сервер...');
      }
      const result = await apiClient.reset();

      if (!result || !result.success) {
        throw new Error('API вернул success: false');
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Сервер успешно сбросил данные');
      }
      hapticFeedback('success');

      // ✅ Локальная очистка ТОЛЬКО после успеха API
      if (process.env.NODE_ENV === 'development') {
        console.log('🧹 Начинаем локальную очистку...');
      }

      // 1. Сбрасываем достижения (с retry)
      if (process.env.NODE_ENV === 'development') {
        console.log('🏆 Сбрасываем достижения...');
      }
      await resetAchievements();

      // 1.5. Сбрасываем profile (счетчики share/referral/feedback)
      if (process.env.NODE_ENV === 'development') {
        console.log('👤 Сбрасываем profile счетчики...');
      }
      resetProfile();

      // 2. Очищаем applications из CloudStorage (с retry - см. ниже)
      if (process.env.NODE_ENV === 'development') {
        console.log('📅 Очищаем applications из CloudStorage...');
      }

      // ✅ FIX: Retry механизм для clearApplicationsFromCloud
      const maxRetries = 3;
      let clearSuccess = false;
      for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
          await clearApplicationsFromCloud();
          clearSuccess = true;
          break;
        } catch (cloudError) {
          if (attempt === maxRetries - 1) {
            // Последняя попытка failed - критическая ошибка
            console.error('❌ КРИТИЧЕСКАЯ ОШИБКА: Не удалось очистить CloudStorage после', maxRetries, 'попыток');
            hapticFeedback('error');
            alert('Критическая ошибка очистки данных. Пожалуйста, попробуйте еще раз или обратитесь в поддержку.');
            return; // Прерываем reset
          } else {
            console.warn(`⚠️ Попытка ${attempt + 1} очистки CloudStorage failed, retry...`);
            await new Promise(r => setTimeout(r, 1000 * Math.pow(2, attempt)));
          }
        }
      }

      // 3. Устанавливаем questionnaireCompleted = false
      setQuestionnaireCompleted(false);
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ questionnaireCompleted установлен в false');
      }

      // 4. Очищаем retinol store
      const retinolStore = useRetinolStore.getState();
      await retinolStore.initializeFromBackend([], []);
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Retinol store очищен');
      }

      // ⚠️ НЕ вызываем checkAndUnlockAchievements здесь!
      // Причина: CloudStorage очищается асинхронно с retry (до 7 сек)
      // Если вызвать checkAndUnlockAchievements раньше, он загрузит старые данные
      // и покажет старые тосты достижений. Достижения пересчитаются автоматически
      // при следующем запуске или переходе на страницу достижений.

      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Локальная очистка завершена');
      }

      // 5. Навигация на /welcome
      if (process.env.NODE_ENV === 'development') {
        console.log('🔄 Редирект на /welcome...');
      }
      navigate('/welcome', { replace: true });

    } catch (error: any) {
      // ❌ API reset failed - НЕ очищаем локальные данные
      console.error('❌ Ошибка при сбросе данных:', {
        message: error?.message,
        status: error?.status,
        error: error,
      });
      hapticFeedback('error');

      // Показываем пользователю понятное сообщение
      const errorMessage = error?.message || 'Неизвестная ошибка';
      alert(`Не удалось сбросить данные: ${errorMessage}\n\nПопробуйте еще раз или обратитесь в поддержку.`);
    } finally {
      setShowResetModal(false);
    }
  };

  const[isEditingTimezone, setIsEditingTimezone] = useState(false);
  const[selectedTimezone, setSelectedTimezone] = useState<string>(
    uiUser.timezone || getUserTimezone()
  );

  const handleTimezoneChange = (newTimezone: string) => {
    updateTimezone(newTimezone);
    setSelectedTimezone(newTimezone);
    setIsEditingTimezone(false);

    if (navigator.vibrate) {
      navigator.vibrate([50, 25, 50]);
    }
  };

  // ✅ КРИТИЧНО: Пересчитываем текущее время при изменении selectedTimezone
  const currentLocalTime = React.useMemo(() => {
    const now = new Date();
    return now.toLocaleString('ru-RU', { timeZone: selectedTimezone });
  }, [selectedTimezone]);

  // ✅ БАГ 3: Handler для toggle уведомлений
  const handleNotificationsToggle = async () => {
    if (isUpdatingNotifications) return;

    const newValue = !notificationsEnabled;
    setIsUpdatingNotifications(true);

    try {
      const response = await apiClient.updateNotifications({
        is_need_notifications: newValue,
      });

      if (response.success) {
        setNotificationsEnabled(newValue);
        hapticFeedback('success');

        // ✅ Сохраняем в CloudStorage для синхронизации между устройствами
        try {
          await syncNotificationsToCloud(newValue);
          if (process.env.NODE_ENV === 'development') {
            console.log('✅ Уведомления обновлены:', newValue ? 'включены' : 'выключены');
          }
        } catch (cloudError) {
          console.error('❌ Ошибка синхронизации notifications в CloudStorage:', cloudError);
          // Не показываем ошибку пользователю, т.к. основная операция успешна
        }
      } else {
        console.error('❌ Ошибка обновления уведомлений:', response.error);
        hapticFeedback('error');
      }
    } catch (error) {
      console.error('❌ Ошибка при обновлении уведомлений:', error);
      hapticFeedback('error');
    } finally {
      setIsUpdatingNotifications(false);
    }
  };

  // ✅ Обработчик клика на "Тема: Системная"
  const handleThemeInfo = () => {
    hapticFeedback(); // Легкий тактильный отклик

    const message = 'Мы автоматически адаптируем оформление приложения под тему, установленную в Вашем Telegram.\n\nЧтобы изменить тему:\n1. Откройте Telegram\n2. Перейдите в Настройки → Внешний вид\n3. Выберите светлую или тёмную тему\n\nПриложение сразу подстроится под Ваш выбор.';

    if (webApp && typeof (webApp as any).showPopup === 'function') {
      try {
        (webApp as any).showPopup(
          {
            title: 'Системная тема',
            message: message,
            buttons: [
              { id: 'ok', type: 'default', text: 'Понятно' }
            ]
          },
          (button_id: string) => {
            if (process.env.NODE_ENV === 'development') {
              console.log('Popup закрыт, button_id:', button_id);
            }
          }
        );
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ Показан popup "Системная тема"');
        }
      } catch (error) {
        console.error('❌ Ошибка showPopup:', error);
        // Fallback на alert
        alert(message);
      }
    } else if (webApp && typeof (webApp as any).showAlert === 'function') {
      // Fallback на showAlert если showPopup не работает
      try {
        (webApp as any).showAlert(message);
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ Показан alert "Системная тема" (fallback)');
        }
      } catch (error) {
        console.error('❌ Ошибка showAlert:', error);
        alert(message);
      }
    } else {
      // Полный fallback на обычный alert
      alert(message);
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Показан alert "Системная тема" (browser fallback)');
      }
    }
  };

  // ✅ БАГ FIX: Запрашиваем данные пользователя (real_name, photo_url) при монтировании
  useEffect(() => {
    const fetchUserInfo = async () => {
      setIsLoadingUserInfo(true);
      try {
        const userInfo = await apiClient.getUserInfo();
        if (process.env.NODE_ENV === 'development') {
          console.log('📥 Получены данные пользователя:', {
            real_name: userInfo.real_name,
            photo_url: userInfo.photo_url,
          });
        }

        // ✅ Обновляем authStore с данными от бекенда (real_name, photo_url)
        // Это вызовет ре-рендер и getDisplayName() пересчитается с новыми данными
        updateUser({
          real_name: userInfo.real_name,
          photo_url: userInfo.photo_url,
        });

        if (process.env.NODE_ENV === 'development') {
          console.log('✅ Данные пользователя обновлены в store, приоритет real_name:', userInfo.real_name);
        }
      } catch (error) {
        console.error('❌ Ошибка получения данных пользователя:', error);
        // Даже при ошибке убираем состояние загрузки, чтобы показать fallback (username или "Гость")
      } finally {
        setIsLoadingUserInfo(false);
      }
    };

    fetchUserInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]); // Запускаем только при монтировании (updateUser стабильная функция из zustand)

  // ✅ Загружаем состояние уведомлений из CloudStorage при монтировании
  useEffect(() => {
    const loadNotifications = async () => {
      try {
        const savedValue = await getNotificationsFromCloud();
        if (savedValue !== null) {
          setNotificationsEnabled(savedValue);
          if (process.env.NODE_ENV === 'development') {
            console.log('✅ Notifications загружены из CloudStorage:', savedValue);
          }
        }
      } catch (error) {
        console.error('❌ Ошибка загрузки notifications из CloudStorage:', error);
      }
    };

    loadNotifications();
  }, []);

  return (
    <Container>
      {/* 👤 HEADER */}
      <ProfileHeader
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Avatar
          $hasPhoto={hasPhotoUrl}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 1.02 }}
        >
          {/* ✅ Показываем фото если есть, иначе emoji */}
          {hasPhotoUrl ? (
            <img src={uiUser.photo_url as string} alt={displayName} />
          ) : (
            avatarEmoji
          )}
        </Avatar>
        <UserName>{displayName}</UserName>
        {uiUser.username && <UserUsername>@{uiUser.username}</UserUsername>}

        <UserStatus>
          <Sparkles size={16} />
          <span>{nextApplicationStatus}</span>
        </UserStatus>
      </ProfileHeader>

      {/* 📊 СТАТИСТИКА */}
      <StatsGrid>
        <StatCard initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3, delay: 0.1 }}>
          <StatIcon><Droplets size={24} /></StatIcon>
          <StatValue>{stats.totalApplications}</StatValue>
          <StatLabel>Применений</StatLabel>
        </StatCard>

        <StatCard initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3, delay: 0.15 }}>
          <StatIcon><Flame size={24} /></StatIcon>
          <StatValue>{stats.currentStreak}</StatValue>
          <StatLabel>Дней подряд</StatLabel>
        </StatCard>

        <StatCard initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3, delay: 0.2 }}>
          <StatIcon><BarChart3 size={24} /></StatIcon>
          <StatValue>{stats.completionRate}%</StatValue>
          <StatLabel>Выполнено</StatLabel>
        </StatCard>
      </StatsGrid>

      {/* 🌍 TIMEZONE СЕКЦИЯ */}
      <Section>
        <SectionTitle>
          <Globe size={18} />
          <span>Часовой пояс</span>
        </SectionTitle>

        <InfoCard initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.4 }}>
          <InfoRow>
            <InfoLabel>
              <Clock size={16} />
              <span>Текущее время</span>
            </InfoLabel>
            <InfoValue>{currentLocalTime}</InfoValue>
          </InfoRow>

          <InfoRowClickable onClick={() => setIsEditingTimezone(!isEditingTimezone)}>
            <InfoLabel>
              <Globe size={16} />
              <span>Часовой пояс</span>
            </InfoLabel>
            <InfoValue>{selectedTimezone}</InfoValue>
          </InfoRowClickable>

          <AnimatePresence initial={false} mode="wait">
            {isEditingTimezone && (
              <TimezoneSelector
                initial={{ maxHeight: 0, opacity: 0 }}
                animate={{ maxHeight: 900, opacity: 1 }}
                exit={{ maxHeight: 0, opacity: 0 }}
                transition={{ duration: 0.25, ease: [0.4, 0.0, 0.2, 1] }}
              >
                <SelectorTitle>Выберите ваш часовой пояс:</SelectorTitle>
                {POPULAR_TIMEZONES.map((tz) => (
                  <TimezoneOption
                    key={tz.value}
                    data-active={selectedTimezone === tz.value}
                    onClick={() => handleTimezoneChange(tz.value)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {tz.label}
                  </TimezoneOption>
                ))}

                <CancelButton onClick={() => setIsEditingTimezone(false)}>
                  Отмена
                </CancelButton>
              </TimezoneSelector>
            )}
          </AnimatePresence>
        </InfoCard>

        <HintText>
          <Sparkles size={16} />
          <span>Часовой пояс используется для напоминаний о применении ретинола.</span>
        </HintText>
      </Section>

      {/* ⚙️ НАСТРОЙКИ */}
      <Section>
        <SectionTitle>
          <User size={18} />
          <span>Настройки</span>
        </SectionTitle>
        <InfoCard initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.45 }}>
          <InfoRow>
            <InfoLabel>
              <Bell size={16} />
              <span>Уведомления</span>
            </InfoLabel>
            <ToggleContainer>
              <ToggleTrack
                $isActive={notificationsEnabled}
                onClick={handleNotificationsToggle}
                disabled={isUpdatingNotifications}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <ToggleThumb $isActive={notificationsEnabled} />
              </ToggleTrack>
            </ToggleContainer>
          </InfoRow>

          <InfoRowClickable onClick={handleThemeInfo}>
            <InfoLabel>
              <Palette size={16} />
              <span>Тема</span>
            </InfoLabel>
            <InfoValue>Системная</InfoValue>
          </InfoRowClickable>
        </InfoCard>
      </Section>

      {/* 🎯 ДЕЙСТВИЯ */}
      <Section>
        <SectionTitle>
          <User size={18} />
          <span>Действия</span>
        </SectionTitle>

        <PrimaryButton
          onClick={handleShare}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.5 }}
        >
          <Share2 size={18} />
          <span>Поделиться прогрессом</span>
        </PrimaryButton>

        <ActionButton
          onClick={handleReferral}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.55 }}
        >
          <Gift size={18} />
          <span>Пригласить друга</span>
        </ActionButton>

        <ActionButton
          onClick={handleFeedbackClick}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.575 }}
        >
          <MessageCircle size={18} />
          <span>Обратная связь</span>
        </ActionButton>

        <ActionButton
          onClick={handleResetClick}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.6 }}
        >
          <RefreshCw size={18} />
          <span>Сбросить данные</span>
        </ActionButton>
      </Section>

      {/* ✅ Модалка "Скопировано" */}
      <AnimatePresence>
        {showCopyModal && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowCopyModal(false)}
          >
            <ModalCard
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalTitle>
                <CheckCircle size={24} color="#22C55E" />
                Скопировано!
              </ModalTitle>
              <ModalText>
                Реферальная ссылка скопирована в буфер обмена.
                Поделитесь ей с друзьями!
              </ModalText>
              <ModalButtonGroup>
                <ModalButton
                  $variant="primary"
                  onClick={() => setShowCopyModal(false)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Понятно
                </ModalButton>
              </ModalButtonGroup>
            </ModalCard>
          </ModalOverlay>
        )}
      </AnimatePresence>

      {/* ✅ Модалка подтверждения сброса */}
      <AnimatePresence>
        {showResetModal && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowResetModal(false)}
          >
            <ModalCard
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalTitle>
                <AlertTriangle size={24} color="#F59E0B" />
                Подтверждение
              </ModalTitle>
              <ModalText>
                Вы уверены? Все данные будут удалены, и придётся
                пройти анкету заново.
              </ModalText>
              <ModalButtonGroup>
                <ModalButton
                  $variant="secondary"
                  onClick={() => setShowResetModal(false)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Отмена
                </ModalButton>
                <ModalButton
                  $variant="primary"
                  onClick={handleResetConfirm}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Сбросить
                </ModalButton>
              </ModalButtonGroup>
            </ModalCard>
          </ModalOverlay>
        )}
      </AnimatePresence>

      {/* ✅ Модалка обратной связи */}
      <AnimatePresence>
        {showFeedbackModal && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowFeedbackModal(false)}
          >
            <ModalCard
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalTitle>
                <MessageCircle size={24} color="#7B68EE" />
                Обратная связь
              </ModalTitle>
              <ModalText>
                Напишите ваше сообщение или предложение
              </ModalText>

              <FeedbackTextarea
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                placeholder="Введите ваше сообщение..."
                maxLength={1000}
                autoFocus
              />

              <ModalButtonGroup>
                <ModalButton
                  $variant="secondary"
                  onClick={() => setShowFeedbackModal(false)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  disabled={isSendingFeedback}
                >
                  Назад
                </ModalButton>
                <ModalButton
                  $variant="primary"
                  onClick={handleSendFeedback}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  disabled={!feedbackText.trim() || isSendingFeedback}
                >
                  {isSendingFeedback ? 'Отправка...' : 'Отправить'}
                </ModalButton>
              </ModalButtonGroup>
            </ModalCard>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </Container>
  );
}
